import type { WallOpening } from "../domain/framing/generateFramedWall";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
} from "../engines/types";
import type { Dictionary } from "../i18n";
import {
	DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
	DEFAULT_DIVIDER_DOOR_ID,
	DEFAULT_DIVIDER_DOOR_WIDTH_MM,
	DEFAULT_FRONT_DOOR_ID,
	DEFAULT_FRONT_DOOR_WIDTH_MM,
	DEFAULT_RELAX_WINDOW_ID,
	getCenteredDividerDoorX,
	getCenteredFrontDoorX,
	getDefaultFrontDoorX,
	getRelaxWindowX,
} from "../pages/builder/defaultOpenings";
import type { SaunaType } from "../types/design";

export interface BuilderTemplate {
	id: string;
	type: SaunaType;
	nameKey: keyof Dictionary;
	descriptionKey: keyof Dictionary;
	floorAreaSqm: number;
	heater: "wood" | "electric";
	state: {
		dimensions: SaunaDimensions;
		partitionSplit?: number;
		openings: WallOpening[];
		deckConfig: DeckConfig | null;
		roofConfig: RoofConfig;
		thermalInputs: Partial<ThermalInputs>;
	};
}

const SQUARE_BACKYARD_WIDTH_M = 2.5;
const SQUARE_BACKYARD_LENGTH_M = 2.0;
const SQUARE_BACKYARD_WIDTH_MM = SQUARE_BACKYARD_WIDTH_M * 1000;

const SQUARE_FAMILY_WIDTH_M = 3.0;
const SQUARE_FAMILY_LENGTH_M = 3.0;
const SQUARE_FAMILY_WIDTH_MM = SQUARE_FAMILY_WIDTH_M * 1000;

const SQUARE_COUPLE_WIDTH_M = 2.0;
const SQUARE_COUPLE_LENGTH_M = 2.0;
const SQUARE_COUPLE_WIDTH_MM = SQUARE_COUPLE_WIDTH_M * 1000;

const TWOROOM_WIDTH_M = 3.5;
// Length is 2.9 m (not 2.5) for two independent reasons:
// 1. The wood heater's mandatory clearance to the upper bench needs it: at
//    2.5 m the partition-split range is empty for a wood heater (min ~0.63 >
//    max 0.60). See getPartitionSplitBounds in
//    src/domain/partition/partitionSplit.ts and builderTemplates.test.ts.
// 2. At 2.7 m (the first fix, split 0.6) the seeded 800mm relax window no
//    longer fit the wall once framing.minCornerClearanceMm was raised — the
//    relax-room segment (0.4 × 2.7m = 1.08m) is narrower than window + 2×
//    clearance (1.10m). 2.9 m gives the relax segment (1.16m) real margin
//    over that floor. See defaultOpenings.ts (relaxWindowFits) and
//    docs/notes/2026-06-22-template-seed-data-must-satisfy-clearance-invariants.md.
const TWOROOM_LENGTH_M = 2.9;
const TWOROOM_WIDTH_MM = TWOROOM_WIDTH_M * 1000;
const TWOROOM_LENGTH_MM = TWOROOM_LENGTH_M * 1000;
const TWOROOM_PARTITION_SPLIT = 0.6;

// Compact two-room: kept deeper (length 3.0) so a wood heater still clears the
// bench within both rooms' BBR minimum lengths — see getPartitionSplitBounds.
const TWOROOM_COMPACT_WIDTH_M = 2.5;
const TWOROOM_COMPACT_LENGTH_M = 3.0;
const TWOROOM_COMPACT_WIDTH_MM = TWOROOM_COMPACT_WIDTH_M * 1000;
const TWOROOM_COMPACT_LENGTH_MM = TWOROOM_COMPACT_LENGTH_M * 1000;
const TWOROOM_COMPACT_PARTITION_SPLIT = 0.55;

const TWOROOM_LAKESIDE_WIDTH_M = 4.0;
const TWOROOM_LAKESIDE_LENGTH_M = 3.0;
const TWOROOM_LAKESIDE_WIDTH_MM = TWOROOM_LAKESIDE_WIDTH_M * 1000;
const TWOROOM_LAKESIDE_LENGTH_MM = TWOROOM_LAKESIDE_LENGTH_M * 1000;
const TWOROOM_LAKESIDE_PARTITION_SPLIT = 0.5;

const TUNNA_WIDTH_M = 2.0;
const TUNNA_LENGTH_M = 2.3;

const RELAX_WINDOW_WIDTH_MM = 800;
const RELAX_WINDOW_HEIGHT_MM = 600;
const LAKESIDE_RELAX_WINDOW_WIDTH_MM = 1200;
const LAKESIDE_RELAX_WINDOW_HEIGHT_MM = 800;

export const BUILDER_TEMPLATES: readonly BuilderTemplate[] = [
	{
		id: "square-backyard",
		type: "fyrkantig",
		nameKey: "template_squareBackyard_name",
		descriptionKey: "template_squareBackyard_description",
		floorAreaSqm: SQUARE_BACKYARD_WIDTH_M * SQUARE_BACKYARD_LENGTH_M,
		heater: "wood",
		state: {
			dimensions: {
				width: SQUARE_BACKYARD_WIDTH_M,
				length: SQUARE_BACKYARD_LENGTH_M,
			},
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getDefaultFrontDoorX(SQUARE_BACKYARD_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
			],
			deckConfig: null,
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
			thermalInputs: {
				heaterType: "wood",
				glassAreaSqm: 0,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "square-family",
		type: "fyrkantig",
		nameKey: "template_squareFamily_name",
		descriptionKey: "template_squareFamily_description",
		floorAreaSqm: SQUARE_FAMILY_WIDTH_M * SQUARE_FAMILY_LENGTH_M,
		heater: "electric",
		state: {
			dimensions: {
				width: SQUARE_FAMILY_WIDTH_M,
				length: SQUARE_FAMILY_LENGTH_M,
			},
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getDefaultFrontDoorX(SQUARE_FAMILY_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
				{
					id: "template-window-side",
					wall: "right",
					type: "window",
					xMm: 800,
					yMm: 1100,
					widthMm: 800,
					heightMm: 600,
					glazed: true,
				},
			],
			deckConfig: {
				shape: "rectangular",
				attachWall: "front",
				depth: 1.8,
				width: SQUARE_FAMILY_WIDTH_M,
				boardType: "28x120",
				joistDimension: "45x145",
			},
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
			thermalInputs: {
				heaterType: "electric",
				glassAreaSqm: 1.5,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "square-couple",
		type: "fyrkantig",
		nameKey: "template_squareCouple_name",
		descriptionKey: "template_squareCouple_description",
		floorAreaSqm: SQUARE_COUPLE_WIDTH_M * SQUARE_COUPLE_LENGTH_M,
		heater: "wood",
		state: {
			dimensions: {
				width: SQUARE_COUPLE_WIDTH_M,
				length: SQUARE_COUPLE_LENGTH_M,
			},
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getDefaultFrontDoorX(SQUARE_COUPLE_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
			],
			deckConfig: null,
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig,
			thermalInputs: {
				heaterType: "wood",
				glassAreaSqm: 0,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "barrel-classic",
		type: "tunna",
		nameKey: "template_barrelClassic_name",
		descriptionKey: "template_barrelClassic_description",
		floorAreaSqm: TUNNA_WIDTH_M * TUNNA_LENGTH_M,
		heater: "wood",
		state: {
			dimensions: { width: TUNNA_WIDTH_M, length: TUNNA_LENGTH_M },
			openings: [],
			deckConfig: null,
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.tunna,
			thermalInputs: {
				heaterType: "wood",
				glassAreaSqm: 0.4,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "tworoom-cabin",
		type: "tvarum",
		nameKey: "template_twoRoomCabin_name",
		descriptionKey: "template_twoRoomCabin_description",
		floorAreaSqm: TWOROOM_WIDTH_M * TWOROOM_LENGTH_M,
		heater: "wood",
		state: {
			dimensions: { width: TWOROOM_WIDTH_M, length: TWOROOM_LENGTH_M },
			partitionSplit: TWOROOM_PARTITION_SPLIT,
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getCenteredFrontDoorX(TWOROOM_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
				{
					id: DEFAULT_DIVIDER_DOOR_ID,
					wall: "divider",
					type: "door",
					xMm: getCenteredDividerDoorX(TWOROOM_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_DIVIDER_DOOR_WIDTH_MM,
					heightMm: DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
					glazed: true,
				},
				{
					id: DEFAULT_RELAX_WINDOW_ID,
					wall: "right",
					type: "window",
					xMm: getRelaxWindowX(
						TWOROOM_LENGTH_MM,
						RELAX_WINDOW_WIDTH_MM,
						TWOROOM_PARTITION_SPLIT,
					),
					yMm: 1150,
					widthMm: RELAX_WINDOW_WIDTH_MM,
					heightMm: RELAX_WINDOW_HEIGHT_MM,
					glazed: true,
				},
			],
			deckConfig: null,
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum,
			thermalInputs: {
				heaterType: "wood",
				glassAreaSqm: 0.5,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "tworoom-compact",
		type: "tvarum",
		nameKey: "template_twoRoomCompact_name",
		descriptionKey: "template_twoRoomCompact_description",
		floorAreaSqm: TWOROOM_COMPACT_WIDTH_M * TWOROOM_COMPACT_LENGTH_M,
		heater: "wood",
		state: {
			dimensions: {
				width: TWOROOM_COMPACT_WIDTH_M,
				length: TWOROOM_COMPACT_LENGTH_M,
			},
			partitionSplit: TWOROOM_COMPACT_PARTITION_SPLIT,
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getCenteredFrontDoorX(TWOROOM_COMPACT_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
				{
					id: DEFAULT_DIVIDER_DOOR_ID,
					wall: "divider",
					type: "door",
					xMm: getCenteredDividerDoorX(TWOROOM_COMPACT_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_DIVIDER_DOOR_WIDTH_MM,
					heightMm: DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
					glazed: true,
				},
				{
					id: DEFAULT_RELAX_WINDOW_ID,
					wall: "right",
					type: "window",
					xMm: getRelaxWindowX(
						TWOROOM_COMPACT_LENGTH_MM,
						RELAX_WINDOW_WIDTH_MM,
						TWOROOM_COMPACT_PARTITION_SPLIT,
					),
					yMm: 1150,
					widthMm: RELAX_WINDOW_WIDTH_MM,
					heightMm: RELAX_WINDOW_HEIGHT_MM,
					glazed: true,
				},
			],
			deckConfig: null,
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum,
			thermalInputs: {
				heaterType: "wood",
				glassAreaSqm: 0.5,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
	{
		id: "tworoom-lakeside",
		type: "tvarum",
		nameKey: "template_twoRoomLakeside_name",
		descriptionKey: "template_twoRoomLakeside_description",
		floorAreaSqm: TWOROOM_LAKESIDE_WIDTH_M * TWOROOM_LAKESIDE_LENGTH_M,
		heater: "electric",
		state: {
			dimensions: {
				width: TWOROOM_LAKESIDE_WIDTH_M,
				length: TWOROOM_LAKESIDE_LENGTH_M,
			},
			partitionSplit: TWOROOM_LAKESIDE_PARTITION_SPLIT,
			openings: [
				{
					id: DEFAULT_FRONT_DOOR_ID,
					wall: "front",
					type: "door",
					xMm: getCenteredFrontDoorX(TWOROOM_LAKESIDE_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
					heightMm: 1900,
					glazed: true,
				},
				{
					id: DEFAULT_DIVIDER_DOOR_ID,
					wall: "divider",
					type: "door",
					xMm: getCenteredDividerDoorX(TWOROOM_LAKESIDE_WIDTH_MM),
					yMm: 0,
					widthMm: DEFAULT_DIVIDER_DOOR_WIDTH_MM,
					heightMm: DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
					glazed: true,
				},
				{
					id: DEFAULT_RELAX_WINDOW_ID,
					wall: "right",
					type: "window",
					xMm: getRelaxWindowX(
						TWOROOM_LAKESIDE_LENGTH_MM,
						LAKESIDE_RELAX_WINDOW_WIDTH_MM,
						TWOROOM_LAKESIDE_PARTITION_SPLIT,
					),
					yMm: 850,
					widthMm: LAKESIDE_RELAX_WINDOW_WIDTH_MM,
					heightMm: LAKESIDE_RELAX_WINDOW_HEIGHT_MM,
					glazed: true,
				},
			],
			deckConfig: {
				shape: "rectangular",
				attachWall: "front",
				depth: 2.0,
				width: TWOROOM_LAKESIDE_WIDTH_M,
				boardType: "28x120",
				joistDimension: "45x145",
			},
			roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum,
			thermalInputs: {
				heaterType: "electric",
				glassAreaSqm: 2.0,
				stoneAreaSqm: 0,
				includesShower: false,
			},
		},
	},
] as const;

export function getTemplatesForType(
	type: SaunaType,
): readonly BuilderTemplate[] {
	if (type === "tunna") return [];
	return BUILDER_TEMPLATES.filter((template) => template.type === type);
}

export function getTemplateById(id: string): BuilderTemplate | undefined {
	return BUILDER_TEMPLATES.find((template) => template.id === id);
}
